import { FC, useEffect, useState } from 'react';
import { Popup } from '~/entities/Popup/Popup';

import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { cvaAddParkingPageLabel } from '~/pages/AddParkingPage/ui/AddParkingPageStyles';
import {
  cvaAddPlacePageInput,
  cvaAddPlacePageErrorMessage,
} from '~/pages/AddPlacePage/ui/AddPlacePageStyle';
import { Input } from '~/shared/ui/Input/Input';
import { useAddCar } from '~/shared/hooks/useAddCar';
import { useGetCars } from '~/shared/hooks/useGetCars';
import { AltInput } from '~/shared/ui/AltInput/AltInput';
import { Portal } from '~/shared/ui/Portal/Portal';
import { useEditCar } from '~/shared/hooks/useEditCar';
import { useDeleteCar } from '~/shared/hooks/useDeleteCar';

interface Props {
  type: 'add' | 'edit';
  auto?: { name: string; number: string; id: number } | null;
  onClose: () => void;
  open: boolean;
}

export const schema = z.object({
  number: z.string(),
  name: z.string(),
});

export type FormData = z.infer<typeof schema>;

export const AddingAutoForm: FC<Props> = ({ type, onClose, auto, open }) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const numberValue = watch('number');
  const nameValue = watch('name');

  const { refetch } = useGetCars();

  const onSuccess = () => {
    refetch();
    onClose();
  };

  const { addCar, errorAdd, isLoading: isLoadingAdd } = useAddCar(onSuccess);
  const {
    editCar,
    errorEdit,
    isLoading: isLoadingEdit,
  } = useEditCar(onSuccess);
  const { deleteCar, isLoading: isLoadingDelete } = useDeleteCar(onSuccess);

  useEffect(() => {
    if (auto) {
      auto.number && setValue('number', auto.number.toUpperCase());
      auto.name && setValue('name', auto.name);
    }
  }, [auto]);

  const onSubmitAdd: SubmitHandler<FormData> = async (data) => {
    const number = data.number.replaceAll(' ', '');
    addCar({ number, name: data.name });
  };

  const onSubmitEdit: SubmitHandler<FormData> = async (data) => {
    const number = data.number.replaceAll(' ', '');
    auto && (await editCar({ number, name: data.name, id: String(auto.id) }));
  };

  const onDelete = async () => {
    auto && (await deleteCar(String(auto.id)));
  };

  return (
    <>
      <form onSubmit={handleSubmit(type == 'add' ? onSubmitAdd : onSubmitEdit)}>
        <Popup
          open={open}
          formPopup
          onClose={onClose}
          title={
            type == 'add'
              ? 'Добавить<br/> автомобиль'
              : 'Редактировать<br/> автомобиль'
          }
          button={{
            text: type == 'add' ? 'Добавить' : 'Сохранить',
            action: () => {
              // handleSubmit(onSubmit);
            },
            disabled: !isValid || !numberValue?.length,
            isLoading: isLoadingAdd || isLoadingEdit,
          }}
          button_second={
            type == 'add'
              ? undefined
              : {
                  text: 'Удалить автомобиль',
                  action: () => {
                    setIsOpen(true);
                    // onClose();
                  },
                  alertButton: true,
                }
          }
          fields={
            <>
              <div className="text-left mt-1.2">
                <label htmlFor={'number'} className={cvaAddParkingPageLabel()}>
                  Государственный номер
                </label>
                <AltInput
                  altInput={false}
                  name={'number'}
                  mask={'A 999 AA 999'}
                  maskChar={''}
                  formatChars={{ '9': '[0-9]', A: '[A-Za-zА-Яа-я]' }}
                  register={register}
                  className={cvaAddPlacePageInput()}
                  placeholder={'A 000 AA 000'}
                  value={numberValue?.toUpperCase() || ''}
                />
                {/* <Input
                className={cvaAddPlacePageInput()}
                register={register}
                name={'number'}
                placeholder={'A 000 AA 000'}
                clearble={!!numberValue}
                clear={() => setValue('number', '')}
                required
              /> */}
                {errors.number && (
                  <p className={cvaAddPlacePageErrorMessage()}>
                    {errors.number.message}
                  </p>
                )}
                {(errorAdd || errorEdit) && (
                  <p className={cvaAddPlacePageErrorMessage()}>
                    {
                      (errorAdd ?? errorEdit)?.response?.data?.errors
                        ?.number?.[0]
                    }
                  </p>
                )}
              </div>
              <div className="text-left mt-1.2">
                <label htmlFor={'name'} className={cvaAddParkingPageLabel()}>
                  Название
                </label>
                <Input
                  className={cvaAddPlacePageInput()}
                  register={register}
                  name={'name'}
                  placeholder={'Не указано'}
                  clearble={!!nameValue}
                  clear={() => setValue('name', '')}
                />
                {errors.name && (
                  <p className={cvaAddPlacePageErrorMessage()}>
                    {errors.name.message}
                  </p>
                )}
                {(errorAdd || errorEdit) && (
                  <p className={cvaAddPlacePageErrorMessage()}>
                    {(errorAdd ?? errorEdit)?.response?.data?.errors?.name?.[0]}
                  </p>
                )}
              </div>
            </>
          }
        />
      </form>
      {isOpen && (
        <Portal>
          <Popup
            open={isOpen}
            title="Вы действительно хотите <br/> удалить автомобиль?"
            button={{
              text: 'Да, удалить',
              action: () => onDelete(),
              alertButton: true,
              isLoading: isLoadingDelete,
            }}
            button_second={{
              text: 'Не удалять',
              action: () => setIsOpen(false),
            }}
          />
        </Portal>
      )}
    </>
  );
};
